import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './AddonForm.css';

const LANG_TABS = [
  { code: 'en', label: 'EN' },
  { code: 'es', label: 'ES' },
  { code: 'ar', label: 'AR' }
];

const AddonForm = ({ menuItemId, onSave, onClose, editAddon = null }) => {
  const { t } = useTranslation();
  const isEdit = !!editAddon;

  const [categoryName, setCategoryName] = useState(editAddon?.categoryName || '');
  const [categoryNameEs, setCategoryNameEs] = useState(editAddon?.categoryNameEs || '');
  const [categoryNameAr, setCategoryNameAr] = useState(editAddon?.categoryNameAr || '');
  const [isRequired, setIsRequired] = useState(!!editAddon?.isRequired);
  const [allowMultiple, setAllowMultiple] = useState(!!editAddon?.allowMultiple);
  const [options, setOptions] = useState(
    editAddon?.options?.length
      ? editAddon.options.map(o => ({
          id: o.id,
          name: o.name || '',
          nameEs: o.nameEs || '',
          nameAr: o.nameAr || '',
          price: Number(o.price) || 0,
          isDefault: !!o.isDefault
        }))
      : [{ name: '', nameEs: '', nameAr: '', price: 0, isDefault: false }]
  );
  const [activeNameTab, setActiveNameTab] = useState('en');
  const [saving, setSaving] = useState(false);

  const addOption = () => {
    setOptions([...options, { name: '', nameEs: '', nameAr: '', price: 0, isDefault: false }]);
  };

  const removeOption = (index) => {
    if (options.length <= 1) return;
    setOptions(options.filter((_, i) => i !== index));
  };

  const updateOption = (index, field, value) => {
    setOptions(options.map((o, i) => (i !== index ? o : { ...o, [field]: value })));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = (categoryName || '').trim();
    if (!name) return;

    const validOptions = options
      .map((o, i) => ({
        ...(o.id != null ? { id: o.id } : {}),
        name: (o.name || '').trim(),
        nameEs: (o.nameEs || '').trim() || null,
        nameAr: (o.nameAr || '').trim() || null,
        price: Math.max(0, Number(o.price) || 0),
        isDefault: !!o.isDefault,
        displayOrder: i
      }))
      .filter(o => o.name);

    if (validOptions.length === 0) return;

    setSaving(true);
    try {
      await onSave({
        categoryName: name,
        categoryNameEs: (categoryNameEs || '').trim() || null,
        categoryNameAr: (categoryNameAr || '').trim() || null,
        isRequired,
        allowMultiple,
        displayOrder: 0,
        options: validOptions
      });
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="addon-form-overlay" onClick={onClose}>
      <div className="addon-form-modal" onClick={e => e.stopPropagation()}>
        <div className="addon-form-header">
          <h3>{isEdit ? t('admin.addons.editCategory') : t('admin.addons.newCategory')}</h3>
          <button type="button" className="addon-form-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="addon-form-body">
          <div className="form-group">
            <label>{t('admin.addons.categoryName')}</label>
            <div className="addon-form-tabs">
              {LANG_TABS.map(tab => (
                <button
                  key={tab.code}
                  type="button"
                  className={`addon-form-tab ${activeNameTab === tab.code ? 'active' : ''}`}
                  onClick={() => setActiveNameTab(tab.code)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            {activeNameTab === 'en' && (
              <input
                type="text"
                className="form-input"
                value={categoryName}
                onChange={e => setCategoryName(e.target.value)}
                placeholder="e.g. SHOT"
                required
              />
            )}
            {activeNameTab === 'es' && (
              <input
                type="text"
                className="form-input"
                value={categoryNameEs}
                onChange={e => setCategoryNameEs(e.target.value)}
                placeholder="e.g. DISPARO"
              />
            )}
            {activeNameTab === 'ar' && (
              <input
                type="text"
                className="form-input"
                value={categoryNameAr}
                onChange={e => setCategoryNameAr(e.target.value)}
                placeholder="e.g. جرعة"
              />
            )}
          </div>

          <div className="addon-form-checkboxes">
            <label className="addon-form-check">
              <input type="checkbox" checked={isRequired} onChange={e => setIsRequired(e.target.checked)} />
              <span>{t('admin.addons.required')}</span>
            </label>
            <label className="addon-form-check">
              <input type="checkbox" checked={allowMultiple} onChange={e => setAllowMultiple(e.target.checked)} />
              <span>{t('admin.addons.allowMultiple')}</span>
            </label>
          </div>

          <div className="form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <label>{t('admin.addons.optionsLabel')}</label>
              <button type="button" className="btn btn-secondary btn-small" onClick={addOption}>
                + {t('admin.addons.addOption')}
              </button>
            </div>
            <div className="addon-form-options-list">
              {options.map((opt, index) => (
                <div key={index} className="addon-form-option-row">
                  <input
                    type="text"
                    className="form-input"
                    placeholder={t('admin.addons.optionName')}
                    value={opt.name}
                    onChange={e => updateOption(index, 'name', e.target.value)}
                    style={{ flex: 1, minWidth: 0 }}
                  />
                  <input
                    type="number"
                    className="form-input"
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    value={opt.price === 0 ? '' : opt.price}
                    onChange={e => updateOption(index, 'price', e.target.value)}
                    style={{ width: '80px' }}
                  />
                  <label className="addon-form-check-inline">
                    <input type="checkbox" checked={opt.isDefault} onChange={e => updateOption(index, 'isDefault', e.target.checked)} />
                    <span>{t('admin.addons.default')}</span>
                  </label>
                  <button type="button" className="btn btn-secondary btn-small" onClick={() => removeOption(index)} disabled={options.length <= 1}>
                    {t('common.remove', 'Remove')}
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="addon-form-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving || !(categoryName || '').trim()}>
              {saving ? '…' : (isEdit ? t('common.save') : t('admin.addons.saveCategory'))}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddonForm;
